import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadTurkishFont, TURKISH_FONT } from '../../shared/utils/pdfTurkishFont';
import { formatFinancialStatementAmount } from './financialPositionPresentation';

const EXCEL_COLORS = {
  title: 'FF16324F',
  header: 'FF16324F',
  summary: 'FFF2F6FB',
  strong: 'FFE1EAF5',
  total: 'FFDCE6F2',
  border: 'FFD3DAE5',
  text: 'FF132238'
};

const PDF_COLORS = {
  title: [22, 50, 79],
  summary: [242, 246, 251],
  strong: [225, 234, 245],
  total: [220, 230, 242],
  text: [19, 34, 56],
  border: [211, 218, 229]
};

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function rowStyleForExcel(variant) {
  if (variant === 'summary') {
    return { fill: EXCEL_COLORS.summary, bold: true };
  }
  if (variant === 'summary-strong') {
    return { fill: EXCEL_COLORS.strong, bold: true };
  }
  if (variant === 'total') {
    return { fill: EXCEL_COLORS.total, bold: true };
  }
  return { fill: null, bold: false };
}

export async function exportEquityChangesToExcel({ presentation, filename, companyName }) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('IFRS_Equity_Changes', {
    views: [{ state: 'frozen', ySplit: 4, xSplit: 1 }]
  });

  const columnCount = 1 + presentation.columns.length;
  sheet.columns = [
    { width: 28 },
    ...presentation.columns.map(() => ({ width: 18 }))
  ];

  sheet.mergeCells(1, 1, 1, columnCount);
  sheet.getCell(1, 1).value = presentation.labels.statementName;
  sheet.getCell(1, 1).font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
  sheet.getCell(1, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_COLORS.title } };
  sheet.getCell(1, 1).alignment = { horizontal: 'center', vertical: 'middle' };

  sheet.mergeCells(2, 1, 2, columnCount);
  sheet.getCell(2, 1).value = companyName || '';
  sheet.getCell(2, 1).font = { size: 10, italic: true, color: { argb: EXCEL_COLORS.text } };
  sheet.getCell(2, 1).alignment = { horizontal: 'center' };

  sheet.addRow([presentation.labels.lineItem, ...presentation.columns.map((column) => column.label)]);
  const headerRow = sheet.getRow(3);
  for (let columnIndex = 1; columnIndex <= columnCount; columnIndex += 1) {
    const cell = headerRow.getCell(columnIndex);
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_COLORS.header } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  }

  presentation.rows.forEach((item) => {
    const row = sheet.addRow([
      item.label,
      ...presentation.columns.map((column) => item.values[column.key] ?? 0)
    ]);
    const style = rowStyleForExcel(item.variant);
    row.font = { bold: style.bold, color: { argb: EXCEL_COLORS.text } };
    if (style.fill) {
      row.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: style.fill } };
      });
    }
    row.eachCell((cell, index) => {
      cell.border = {
        top: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        left: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        bottom: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        right: { style: 'thin', color: { argb: EXCEL_COLORS.border } }
      };
      cell.alignment = { vertical: 'middle', horizontal: index === 1 ? 'left' : 'right' };
      if (index > 1) {
        cell.numFmt = '#,##0.00';
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename
  );
}

export async function exportEquityChangesToPdf({ presentation, filename, companyName }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  await loadTurkishFont(doc);
  doc.setFont(TURKISH_FONT, 'normal');

  doc.setFontSize(15);
  doc.setTextColor(...PDF_COLORS.title);
  doc.text(presentation.labels.statementName, 148.5, 14, { align: 'center' });

  if (companyName) {
    doc.setFontSize(10);
    doc.setTextColor(...PDF_COLORS.text);
    doc.text(companyName, 148.5, 20, { align: 'center' });
  }

  const rows = presentation.rows;
  autoTable(doc, {
    startY: companyName ? 26 : 20,
    head: [[presentation.labels.lineItem, ...presentation.columns.map((column) => column.label)]],
    body: rows.map((row) => ([
      row.label,
      ...presentation.columns.map((column) => formatFinancialStatementAmount(row.values[column.key] ?? 0, presentation.locale))
    ])),
    margin: { left: 8, right: 8 },
    theme: 'plain',
    styles: {
      font: TURKISH_FONT,
      fontSize: 7,
      textColor: PDF_COLORS.text,
      lineColor: PDF_COLORS.border,
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: PDF_COLORS.title,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center'
    },
    didParseCell: (hook) => {
      if (hook.section !== 'body') return;
      const row = rows[hook.row.index];
      if (row.variant === 'summary') {
        hook.cell.styles.fillColor = PDF_COLORS.summary;
        hook.cell.styles.fontStyle = 'bold';
      } else if (row.variant === 'summary-strong') {
        hook.cell.styles.fillColor = PDF_COLORS.strong;
        hook.cell.styles.fontStyle = 'bold';
      } else if (row.variant === 'total') {
        hook.cell.styles.fillColor = PDF_COLORS.total;
        hook.cell.styles.fontStyle = 'bold';
      }
    }
  });

  doc.save(filename);
}
